<script lang="ts">
  import { isCartUpdating } from '../store/store'
  import Money from './Money.svelte'

  export let item: {
    handle?: string
    variant_id: number
    quantity: number
    selling_plan_allocation?: {
      selling_plan: { id: number; name: string }
      price: number
      compare_at_price: number
    }
    properties?: Record<string, string>
  }
  export let line: number
  export let onConverted: () => void | Promise<void>

  type SellingPlanAllocation = {
    selling_plan_id: number
    selling_plan_group_id: string
    price: number
    compare_at_price: number
  }

  type SellingPlan = {
    id: number
    name: string
  }

  type SellingPlanGroup = {
    id: string
    name: string
    selling_plans: SellingPlan[]
  }

  type Product = {
    variants: ProductVariant[]
    selling_plan_groups: SellingPlanGroup[]
  }

  type ProductVariant = {
    id: number
    price: number
    selling_plan_allocations?: SellingPlanAllocation[]
  }

  const productCache = new Map<string, Product>()

  let expanded = false
  let allocations: SellingPlanAllocation[] = []
  let sellingPlanGroups: SellingPlanGroup[] = []
  let oneTimePrice = 0
  let loading = false
  let converting = false

  $: isBundle = Boolean(item.properties?._rc_bundle)
  $: showUpsell = !isBundle && Boolean(item.handle)
  $: currentPlanId = item.selling_plan_allocation?.selling_plan?.id ?? null
  $: isSubscribed = currentPlanId !== null

  let loadedKey = ''

  $: if (showUpsell && item.handle) {
    const key = `${item.handle}:${item.variant_id}`
    if (key !== loadedKey) {
      loadedKey = key
      loadSellingPlans(item.handle, item.variant_id)
    }
  }

  async function loadSellingPlans(handle: string, variantId: number) {
    if (loading) return
    loading = true

    try {
      let product = productCache.get(handle)
      if (!product) {
        const response = await fetch(`/products/${handle}.js`)
        if (!response.ok) return
        product = await response.json()
        productCache.set(handle, product)
      }

      sellingPlanGroups = product?.selling_plan_groups ?? []

      const variant = product?.variants?.find((v) => v.id === variantId)
      allocations = variant?.selling_plan_allocations ?? []
      oneTimePrice = variant?.price ?? 0
    } finally {
      loading = false
    }
  }

  function getPlanName(planId: number) {
    for (const group of sellingPlanGroups) {
      const plan = group.selling_plans.find((p) => p.id === planId)
      if (plan) return plan.name
    }
    return 'Subscription'
  }

  function getMaxDiscountPercentage() {
    let max = 0
    for (const allocation of allocations) {
      if (allocation.compare_at_price > 0) {
        const pct = Math.round((1 - allocation.price / allocation.compare_at_price) * 100)
        if (pct > max) max = pct
      }
    }
    return max
  }

  async function changeSellingPlan(planId: number | null) {
    if (planId === currentPlanId) {
      expanded = false
      return
    }

    converting = true
    $isCartUpdating = true

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line,
          quantity: item.quantity,
          selling_plan: planId,
        }),
      })

      if (!response.ok) {
        $isCartUpdating = false
        return
      }

      expanded = false
      await onConverted()
    } finally {
      converting = false
    }
  }
</script>

{#if showUpsell && allocations.length > 0}
  <div class="rc-widget mt-3">
    {#if !expanded}
      <button
        type="button"
        class="block w-full text-center rounded-xl bg-wagLight bg-opacity-50 py-2 px-6 text-sm font-medium disabled:opacity-50"
        disabled={converting || $isCartUpdating}
        on:click={() => (expanded = true)}
      >
        {#if isSubscribed}
          Change delivery frequency
        {:else if getMaxDiscountPercentage() > 0}
          Subscribe and save {getMaxDiscountPercentage()}%
        {:else}
          Subscribe &amp; save
        {/if}
      </button>
    {:else}
      <div class="rounded-lg border border-wagLight bg-wagLight bg-opacity-50 p-3 space-y-2">
        <p class="text-sm font-medium text-gray-900">
          {#if isSubscribed}
            Delivery frequency
          {:else}
            Subscribe &amp; save
          {/if}
        </p>

        {#if isSubscribed}
          <button
            type="button"
            class="rc-radio flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:border-primary disabled:opacity-50"
            disabled={converting || $isCartUpdating}
            on:click={() => changeSellingPlan(null)}
          >
            <span>One-time purchase</span>
            <span class="rc-radio__price">
              <Money price={oneTimePrice * item.quantity} showCurrency={true} />
            </span>
          </button>
        {/if}

        {#each allocations as allocation (allocation.selling_plan_id)}
          {@const isCurrent = allocation.selling_plan_id === currentPlanId}
          <button
            type="button"
            class="rc-radio border-gray-200 bg-white hover:border-primary disabled:bg-opacity-50 diabled:border-primary flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm"
            disabled={converting || $isCartUpdating || isCurrent}
            on:click={() => changeSellingPlan(allocation.selling_plan_id)}
          >
            <span>
              {getPlanName(allocation.selling_plan_id)}
              {#if isCurrent}
                <span class="ml-1 text-xs text-primary">(current)</span>
              {/if}
            </span>
            <span class="rc-radio__price">
              <Money price={allocation.price * item.quantity} showCurrency={true} />
            </span>
          </button>
        {/each}

        <button
          type="button"
          class="text-xs  underline hover:no-underline"
          disabled={converting}
          on:click={() => (expanded = false)}
        >
          Cancel
        </button>
      </div>
    {/if}
  </div>
{/if}
